import { computeUnloadTiles } from "@dailywars/core/engine";
import type { Coord, Move } from "@dailywars/core/engine";

import type { StoreState } from "#/game/store/types";

import { applyMoveAction } from "./applyMove";

export function commitWaitOrMove({
  state,
  unitId,
  path,
}: {
  state: StoreState;
  unitId: string;
  path: Coord[];
}): StoreState {
  if (path.length === 0) {
    return applyMoveAction({ state, move: { type: "wait", unitId } });
  }
  return applyMoveAction({
    state,
    move: { type: "move", unitId, path },
    options: { suppressMoveAnimation: true },
  });
}

export function commitCapture({
  state,
  unitId,
  path,
}: {
  state: StoreState;
  unitId: string;
  path: Coord[];
}): StoreState {
  if (path.length === 0) {
    return applyMoveAction({ state, move: { type: "capture", unitId } });
  }
  const afterMove = applyMoveAction({
    state,
    move: { type: "move", unitId, path },
    options: { suppressMoveAnimation: true },
  });
  return applyMoveAction({ state: afterMove, move: { type: "capture", unitId } });
}

export function commitJoin({
  state,
  unitId,
  targetId,
  path,
}: {
  state: StoreState;
  unitId: string;
  targetId: string;
  path: Coord[];
}): StoreState {
  return applyMoveAction({
    state,
    move: { type: "join", unitId, targetId, path },
    options: { suppressMoveAnimation: true },
  });
}

export function commitLoad({
  state,
  unitId,
  transportId,
  path,
}: {
  state: StoreState;
  unitId: string;
  transportId: string;
  path: Coord[];
}): StoreState {
  return applyMoveAction({
    state,
    move: { type: "load", unitId, transportId, path },
    options: { suppressMoveAnimation: true },
  });
}

export function selectTransportUnload(state: StoreState, unitId: string): StoreState {
  const unit = state.localState.units.find((u) => u.id === unitId);
  if (!unit?.cargo) return { ...state, selection: { mode: "none" } };
  return {
    ...state,
    selection: {
      mode: "transport-unload",
      unitId,
      unloadTiles: computeUnloadTiles(state.localState, unit),
    },
  };
}

export function commitPrepareUnload(state: StoreState, unitId: string): StoreState {
  const after = applyMoveAction({ state, move: { type: "prepareUnload", unitId } });
  return selectTransportUnload(after, unitId);
}

export function commitUnload({
  state,
  unitId,
  pos,
}: {
  state: StoreState;
  unitId: string;
  pos: Coord;
}): StoreState {
  return applyMoveAction({ state, move: { type: "unload", unitId, pos } });
}

export function commitWait(state: StoreState, unitId: string): StoreState {
  return applyMoveAction({ state, move: { type: "wait", unitId } });
}

export function commitAttack(state: StoreState): StoreState {
  if (state.selection.mode !== "confirm-attack") return state;
  const sel = state.selection;
  const targetFields =
    sel.targetKind === "cannon" ? { targetPos: sel.targetPos } : { targetId: sel.targetId! };
  const move: Move =
    sel.path.length > 0
      ? { type: "attack", unitId: sel.unitId, ...targetFields, path: sel.path }
      : { type: "attack", unitId: sel.unitId, ...targetFields };
  return applyMoveAction({
    state,
    move,
    options: {
      suppressMoveAnimation: state.selection.path.length > 0,
    },
  });
}

export function completeAnimation(state: StoreState): StoreState {
  if (!state.animation) return state;
  const nowMs = typeof performance !== "undefined" ? performance.now() : 0;

  if (state.animationQueue.length > 0) {
    const [next, ...rest] = state.animationQueue;
    return {
      ...state,
      animation: { ...next, startedAtMs: nowMs },
      animationQueue: rest,
    };
  }

  return { ...state, animation: null, animationQueue: [] };
}
